import React, {useState, useEffect} from "react";

import db from "../../../services/query";

import PrintHeader from "../common/PrintHeader";
import PrintBodyTitle from "../common/PrintBodyTitle";
import PrintTable from "../common/PrintTable";
import RowPhoto from "../../common/TdImg";

const underHundred = (value) => {
    const ones = ["", "یک", "دو", "سه", "چهار", "پنج", "شش", "هفت", "هشت", "نه"];
    const teens = ["ده", "یازده", "دوازده", "سیزده", "چهارده", "پانزده", "شانزده", "هفده", "هجده", "نوزده"];
    const tens = ["", "", "بیست", "سی", "چهل", "پنجاه", "شصت", "هفتاد", "هشتاد", "نود"];
    if (value < 10) return ones[value];
    if (value < 20) return teens[value - 10];
    const ten = Math.floor(value / 10);
    const one = value % 10;
    return one ? `${tens[ten]} و ${ones[one]}` : tens[ten];
};

const integerInWords = (value) => {
    if (value === 0) return "صفر";
    if (value === 100) return "صد";
    if (value < 100) return underHundred(value);
    const hundreds = Math.floor(value / 100);
    const rest = value % 100;
    const hundredWord = hundreds === 1 ? "صد" : `${underHundred(hundreds)} صد`;
    return rest ? `${hundredWord} و ${underHundred(rest)}` : hundredWord;
};

const totalInWords = (total) => {
    if (total === null || total === undefined || total === "") return "";
    const value = Number(total);
    if (!Number.isFinite(value) || value < 0) return "";
    const whole = Math.trunc(value);
    const words = integerInWords(whole);
    const fractionText = String(value).split(".")[1];
    if (!fractionText) return words;
    const fraction = Number(fractionText);
    if (!Number.isFinite(fraction)) return words;
    return `${words} ممیز ${integerInWords(fraction)}`;
};

const isCompleteZero = (total) => {
    if (total === null || total === undefined || total === "") return false;
    const value = Number(total);
    return Number.isFinite(value) && value === 0;
};

const ResultBrief = ({ rows }) => {
    const list = Array.isArray(rows) ? rows : [];
    let passed = 0;
    let failed = 0;
    list.forEach((row) => {
        // Zero total / محروم count as ناکام (no separate محرومین row).
        if (isCompleteZero(row?.total) || Number(row?.result) === 3) failed += 1;
        else if (Number(row?.result) === 1) passed += 1;
        else if (Number(row?.result) === 0) failed += 1;
    });
    const items = [
        ["تعداد شاملین امتحان", list.length],
        ["تعداد کامیاب ها", passed],
        ["تعداد ناکام ها", failed],
    ];
    return (
        <aside className="shoqa-result-brief" aria-label="خلص نتایج">
            <h4>خلص نتایج</h4>
            {items.map(([label, value]) => (
                <div key={label}>
                    <span>{label}</span>
                    <b>{value}</b>
                </div>
            ))}
        </aside>
    );
};

const studentCode = (row) =>
    row?.aria_id || row?.student_code || row?.tawana_id || "";

const StudentNameCell = ({ row }) => {
    // student_name already includes تخلص — only change the column title.
    const name = row?.student_name || "";
    const code = studentCode(row);
    return (
        <div className="shoqa-student-name-cell">
            <span className="shoqa-student-name">{name}</span>
            {code ? <span className="shoqa-student-code">{code}</span> : null}
        </div>
    );
};

/** A4 portrait printable table width fallback (CSS px). */
const RESULT_TABLE_PX = 680;
const CELL_PAD_PX = 8;
const SERIAL_MIN_PX = 26;

const NAME_FONT = '400 11px "IRANSans", Tahoma, sans-serif';
const CODE_FONT = '400 10px "IRANSans", Tahoma, sans-serif';
const HEADER_FONT = '700 10px "IRANSans", Tahoma, sans-serif';

let _measureCanvas;
const measureTextPx = (text, font) => {
    if (text === null || text === undefined || text === "") return 0;
    if (typeof document === "undefined") return String(text).length * 7;
    if (!_measureCanvas) _measureCanvas = document.createElement("canvas");
    const ctx = _measureCanvas.getContext("2d");
    if (!ctx) return String(text).length * 7;
    ctx.font = font;
    return ctx.measureText(String(text)).width;
};

const SCORE_HEADER_LABELS = [
    "فعالیت صنفی",
    "کارخانگی",
    "وسط سمستر",
    "نهایی",
    "مجموع به عدد",
    "مجموع به حروف",
    "نتیجه",
    "ملاحظه",
];

/**
 * Size شماره / نام / پدر to content; give leftover width to score columns
 * so headers (فعالیت صنفی, …) stay on one line without overlap.
 */
const computeResultColWidths = (rows, tablePx = RESULT_TABLE_PX) => {
    const list = Array.isArray(rows) ? rows : [];
    const tableW = Math.max(320, Number(tablePx) || RESULT_TABLE_PX);

    let namePx = measureTextPx("نام و تخلص", HEADER_FONT);
    let fatherPx = measureTextPx("نام پدر", HEADER_FONT);

    list.forEach((row) => {
        const name = row?.student_name || "";
        const code = studentCode(row);
        const father = row?.student_father_name || "";
        namePx = Math.max(
            namePx,
            measureTextPx(name, NAME_FONT),
            measureTextPx(code, CODE_FONT)
        );
        fatherPx = Math.max(fatherPx, measureTextPx(father, NAME_FONT));
    });

    namePx = Math.ceil(namePx) + CELL_PAD_PX;
    fatherPx = Math.ceil(fatherPx) + CELL_PAD_PX;
    let serialPx = Math.max(
        SERIAL_MIN_PX,
        Math.ceil(measureTextPx("شماره", HEADER_FONT)) + 6
    );

    const scoreMins = SCORE_HEADER_LABELS.map(
        (label) => Math.ceil(measureTextPx(label, HEADER_FONT)) + CELL_PAD_PX
    );
    const scoreMinTotal = scoreMins.reduce((a, b) => a + b, 0);

    let remaining = tableW - serialPx - namePx - fatherPx;
    if (remaining < scoreMinTotal && namePx + fatherPx > 0) {
        // Prefer readable score headers; shrink name/father only as needed.
        const keepForScores = Math.min(scoreMinTotal, Math.floor(tableW * 0.62));
        const budget = tableW - serialPx - keepForScores;
        const scale = budget / (namePx + fatherPx);
        if (scale > 0 && scale < 1) {
            namePx = Math.max(52, Math.floor(namePx * scale));
            fatherPx = Math.max(44, Math.floor(fatherPx * scale));
        }
        remaining = tableW - serialPx - namePx - fatherPx;
    }
    if (remaining < 1) remaining = 1;

    const minSum = scoreMins.reduce((a, b) => a + b, 0) || 1;
    let scorePxs;
    if (minSum >= remaining) {
        scorePxs = scoreMins.map((m) => (m / minSum) * remaining);
    } else {
        const extra = remaining - minSum;
        scorePxs = scoreMins.map((m) => m + (extra * m) / minSum);
    }

    const toPct = (px) => `${((px / tableW) * 100).toFixed(2)}%`;
    return [toPct(serialPx), toPct(namePx), toPct(fatherPx), ...scorePxs.map(toPct)];
};

const ResultOrShoqa = (props) => {
    const {isShoqa, title, chance} = props;

    const [data, setData] = useState([]);

    const subjectTitle = (() => {
        const name = data?.summary?.title || "";
        const code = data?.summary?.subject_code || "";
        if (name && code) return `${name} - ${code}`;
        return name || code || "";
    })();

    let titles = [
        title ?? '',
        `${data?.summary?.class_desc ?? ''} | ${data?.summary?.entry_year ?? ''}`,
        subjectTitle,
        `${data?.summary?.credits ? data.summary.credits + ' کریدت ' : ''}`,
        data?.summary?.teacher_name,
    ].filter((label) => label !== null && label !== undefined && String(label).trim() !== "")

    let printHeadTexts = [
        "معاونیت امور محصلان",
        data?.summary?.faculty_name
    ];

    const renderResult = (row) => {
        // Complete 0 / legacy محروم status both display as ناکام on this print.
        if (isCompleteZero(row?.total) || Number(row?.result) === 3) return "ناکام";
        switch (Number(row?.result)) {
            case 0:
                return "ناکام";
            case 1:
                return "کامیاب";
            default:
                return "";
        }
    };
    const renderScoreBox = (e, field) => {
        const value = isShoqa ? "" : e[field];
        const display =
            value === null || value === undefined || value === ""
                ? ""
                : value;
        return (
            <span className="shoqa-score-box">{display}</span>
        );
    };

    // Result sheet: شماره (no عکس) + content-sized name/father; shoqa keeps full grid.
    const columns = [
        ...(isShoqa
            ? [
                  {path: "id", label: "شماره"},
                  {
                      path: "student_photo",
                      label: "عکس",
                      content: ({student_photo}) => <RowPhoto src={student_photo}/>,
                  },
              ]
            : [{path: "id", label: "شماره"}]),
        {
            path: "student_name",
            label: "نام و تخلص",
            content: (row) => <StudentNameCell row={row} />,
        },
        {path: "student_father_name", label: "نام پدر"},
        {
            path: "class_activity",
            label: "فعالیت صنفی",
            content: (e) => renderScoreBox(e, "class_activity"),
        },
        {
            path: "homework",
            label: "کارخانگی",
            content: (e) => renderScoreBox(e, "homework"),
        },
        {
            path: "mid_term",
            label: "وسط سمستر",
            content: (e) => renderScoreBox(e, "mid_term"),
        },
        {
            path: "final",
            label: "نهایی",
            content: (e) => renderScoreBox(e, "final"),
        },
        {
            path: "total",
            label: "مجموع به عدد",
            content: (e) => renderScoreBox(e, "total"),
        },
    ];

    if (!isShoqa) {
        columns.push(
            {
                path: "total_words",
                label: "مجموع به حروف",
                content: (row) => <span className="shoqa-total-words">{totalInWords(row?.total)}</span>,
            },
            {
                path: "result",
                label: "نتیجه",
                content: (row) => renderResult(row),
            },
            {
                path: "remark",
                label: "ملاحظه",
                content: (row) => <span className="shoqa-remark">{row?.remark || ""}</span>,
            }
        );
    }

    const [resultColWidths, setResultColWidths] = useState(undefined);

    useEffect(() => {
        requestData();

        return () => setData([]);
    }, []);

    useEffect(() => {
        if (isShoqa) {
            setResultColWidths(undefined);
            return;
        }
        const rows = data?.data;
        if (!Array.isArray(rows) || rows.length === 0) {
            setResultColWidths(undefined);
            return;
        }

        let cancelled = false;
        const apply = () => {
            if (cancelled) return;
            const tableArea = document.querySelector(
                ".main-print-wrapper.shoqa-result .print-table-area"
            );
            const tableW = tableArea?.clientWidth || RESULT_TABLE_PX;
            setResultColWidths(computeResultColWidths(rows, tableW));
        };

        const run = () => {
            if (document.fonts?.ready) {
                document.fonts.ready.then(apply).catch(apply);
            } else {
                apply();
            }
        };

        run();
        const t = window.setTimeout(run, 50);
        window.addEventListener("resize", run);
        return () => {
            cancelled = true;
            window.clearTimeout(t);
            window.removeEventListener("resize", run);
        };
    }, [data, isShoqa]);

    const resultColVars =
        !isShoqa && resultColWidths
            ? {
                  "--sr-serial-w": resultColWidths[0],
                  "--sr-name-w": resultColWidths[1],
                  "--sr-father-w": resultColWidths[2],
                  "--sr-score-a-w": resultColWidths[3],
                  "--sr-score-b-w": resultColWidths[4],
                  "--sr-score-c-w": resultColWidths[5],
                  "--sr-score-d-w": resultColWidths[6],
                  "--sr-score-e-w": resultColWidths[7],
                  "--sr-words-w": resultColWidths[8],
                  "--sr-result-w": resultColWidths[9],
                  "--sr-remark-w": resultColWidths[10],
              }
            : undefined;

    const requestData = async (p = {}) => {
        const {classId, subjectId, entryYearId} = props.match.params;

        try {
            let query = `results/classes/${classId}/${subjectId}?&entryYearId=${entryYearId}`;

            await db.getAll(query).then(({data}) => {

                // filter data for each shoqa based on chance
                if(isShoqa && chance > 1){
                    data.data = data.data.filter((result) => result.chance_no == (chance - 1) && result.result === 0);
                }

                setData(data);
            });

        } catch ({response}) {
            setData(response);
        }
    };

    return (
        <main
            className={`main-print-wrapper no-table-footer shoqa${isShoqa ? "" : " shoqa-result"}`}
            style={resultColVars}
        >
            <PrintHeader
                titles={titles}
                printHeadTexts={printHeadTexts}
                changeableDate={isShoqa}
                hideDate={!isShoqa}
                date={isShoqa ? "" : "  "}
                landscape={false}
                textsExtra={isShoqa ? null : <ResultBrief rows={data?.data} />}
            />
            <PrintBodyTitle title={[]}/>
            <PrintTable
                columns={columns}
                data={data}
                colWidths={isShoqa ? undefined : resultColWidths}
            />

            <div style={{display: "flex", placeItems: "center", paddingTop: "50px"}}>
                <div className="print-identity">
                    <div className="print-signature-area">
                        <div className="wrapper" style={{width: "80%"}}>
                            <hr/>
                            <p>امضاء آمر دیپارتمنت</p>
                        </div>
                    </div>
                </div>

                <div className="print-identity">
                    <div className="print-signature-area">
                        <div className="wrapper" style={{width: "80%"}}>
                            <hr/>
                            <p> امضاء ممیز</p>
                        </div>
                    </div>
                </div>

                <div className="print-identity">
                    <div className="print-signature-area">
                        <div className="wrapper" style={{width: "80%"}}>
                            <hr/>
                            <p>امضاء استاد</p>
                        </div>
                    </div>
                </div>
            </div>
        </main>
    );
};

export default ResultOrShoqa;
